import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useQuery } from 'react-apollo';

import Select from '../../../../../Select';
import {
  CARD_SIDE,
  ORIENTATION,
} from '../../../../../../../services/cards/constants';
import SecondaryControlButton from '../../../../../../../services/prints/components/PrintProductConfigurator/components/PrintProductConfiguratorControlsPane/SecondaryControlButton';
import SecondaryControlButtonSizeRectangle from '../../../../../../../services/prints/components/PrintProductConfigurator/components/PrintProductConfiguratorControlsPane/SecondaryControlButton/SecondaryControlButtonSizeRectangle';
import getPrintPartners from '../../../../../../../services/cards/graphql/queries/getPrintPartners';
import { getCardTemplateSizing } from '../../../../../../../services/cards/components/AccountCard/helpers';
import {
  getCardSide,
  getPrintProductSizeFromPrintSize,
  getPrintSizeTitle,
} from '../../../../helpers';
import getPrintProductsForCardDesigner from '../../../../../../../services/cards/graphql/queries/getPrintProductsForCardDesigner';
import useRequestConfirm from '../../../../../../../hooks/useRequestConfirm';

const propTypes = {
  className: PropTypes.string,
  onCardSettingsUpdate: PropTypes.func.isRequired,
  card: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  refetchCard: PropTypes.func,
  printPartnerId: PropTypes.string,
};

const CardDesignerLayoutControls = ({
  className,
  onCardSettingsUpdate,
  card,
  refetchCard,
  printPartnerId,
}) => {
  const requestConfirm = useRequestConfirm();
  const classes = classNames({
    [className]: !!className,
  });

  const { data: { printPartners } = {} } = useQuery(getPrintPartners, {
    variables: {
      // Fallback to bay
      name: printPartnerId ? undefined : 'Bay Photo',
      id: printPartnerId,
    },
  });

  const [printPartner] = printPartners?.records ?? [];

  const { data: { printProducts: printProductsResult } = {} } = useQuery(
    getPrintProductsForCardDesigner,
    {
      variables: {
        print_partner_id: printPartner?.id,
      },
      skip: !printPartner?.id,
    },
  );

  const cardSide = getCardSide({ card, side: CARD_SIDE.FRONT });
  const cardTemplate = cardSide?.card_template;
  const orientation = cardTemplate?.orientation ?? ORIENTATION.LANDSCAPE;
  const printProducts = printProductsResult?.records ?? [];
  const standardPrintProduct = printProducts[0];
  const printProductSizes = useMemo(
    () =>
      (standardPrintProduct?.sizes?.records ?? [])
        .filter(({ orientations }) => orientations.includes(orientation))
        .sort((a, b) => {
          if (
            a.print_size?.name?.includes('Memory Mate 8x10') ||
            a.print_size?.name?.includes('Memory Mate 10x8')
          ) {
            return -1;
          }

          if (
            b.print_size?.name?.includes('Memory Mate 8x10') ||
            b.print_size?.name?.includes('Memory Mate 10x8')
          ) {
            return 1;
          }

          return 0;
        }),
    [standardPrintProduct, orientation],
  );

  return (
    <div className={classes}>
      <Select
        id="card_designer_orientation"
        name="card_designer_orientation"
        className="mb-2"
        value={orientation}
        size="sm"
        onChange={async event => {
          const { value } = event.target;
          const currentPrintProductSize = getPrintProductSizeFromPrintSize({
            printProduct: standardPrintProduct,
            printSize: cardTemplate?.print_size,
          });
          const orientationIsSupported = (
            currentPrintProductSize?.orientations ?? []
          ).includes(value);

          if (orientationIsSupported) {
            await onCardSettingsUpdate({
              cardTemplateId: cardTemplate?.id,
              values: {
                orientation: value,
              },
            });
          } else {
            requestConfirm({
              message: (
                <p>
                  The selected print size does not support the new orientation.
                  Switching orientations will result in changing the template
                  back to its default state.
                  <br />
                  <br />
                  Are you sure you want to continue?
                </p>
              ),
              confirmText: 'Confirm',
              cancelText: 'Cancel',
              onConfirm: async () => {
                const defaultPrintProductSize =
                  getPrintProductSizeFromPrintSize({
                    printProduct: standardPrintProduct,
                    useDefault: true,
                  });

                await onCardSettingsUpdate({
                  cardTemplateId: cardTemplate?.id,
                  printProductCardId: standardPrintProduct?.card?.id,
                  cardSide,
                  values: {
                    print_size_id: defaultPrintProductSize.print_size.id,
                    orientation: value,
                  },
                });
              },
              onCancel: () => {},
            });
          }
        }}
        label="Orientation"
      >
        <option value={ORIENTATION.LANDSCAPE}>Landscape</option>
        <option value={ORIENTATION.PORTRAIT}>Portrait</option>
      </Select>

      <div className="card-designer-layout-controls-list">
        {printProductSizes.map(printProductSize => {
          const printSize = printProductSize.print_size;
          const { width, height } = getCardTemplateSizing({
            orientation,
            print_size: {
              width: printSize.width,
              height: printSize.height,
            },
          });

          const title = getPrintSizeTitle({ printSize, width, height });

          return (
            <SecondaryControlButton
              key={printProductSize.id}
              title={title}
              icon={
                <SecondaryControlButtonSizeRectangle
                  width={width}
                  height={height}
                  orientation={orientation}
                />
              }
              onClick={async () => {
                await onCardSettingsUpdate({
                  cardTemplateId: cardTemplate?.id,
                  printProductCardId: standardPrintProduct?.card?.id,
                  cardSide,
                  values: {
                    print_size_id: printProductSize.print_size.id,
                    orientation,
                  },
                });
                await refetchCard?.();
              }}
              active={
                cardTemplate?.print_size?.id === printProductSize.print_size.id
              }
            />
          );
        })}
      </div>
    </div>
  );
};

CardDesignerLayoutControls.propTypes = propTypes;

export default CardDesignerLayoutControls;
